/**
 * 加密工具模块
 * - 浏览器指纹生成
 * - AES-CBC 解密（兼容 CryptoJS/OpenSSL 格式）
 */

/**
 * 获取当前设备的公网 IP
 * 通过 ip138.com 获取，用于解密 peer 参数
 * @returns 公网 IP 字符串
 */
export async function getPublicIP(): Promise<string> {
  const resp = await fetch('https://2026.ip138.com/');
  const html = await resp.text();
  // 从 title 中提取 IP: <title>您的IP地址是：106.81.151.189</title>
  const match = html.match(/<title>您的IP地址是：([\d.]+)<\/title>/);
  if (!match) {
    throw new Error('Failed to extract IP from ip138.com');
  }
  return match[1];
}

/**
 * MD5 哈希实现（用于 EVP_BytesToKey）
 * 简化版，仅用于密钥派生
 */
function md5(data: Uint8Array): Uint8Array {
  // MD5 constants
  const S = [
    7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
    5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
    4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
    6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
  ];

  const K = new Uint32Array([
    0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a,
    0xa8304613, 0xfd469501, 0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be,
    0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821, 0xf61e2562, 0xc040b340,
    0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
    0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8,
    0x676f02d9, 0x8d2a4c8a, 0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c,
    0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70, 0x289b7ec6, 0xeaa127fa,
    0xd4ef3085, 0x04881d05, 0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
    0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92,
    0xffeff47d, 0x85845dd1, 0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1,
    0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
  ]);

  const leftRotate = (x: number, c: number) => (x << c) | (x >>> (32 - c));

  // Pre-processing: add padding
  const origLen = data.length;
  const newLen = ((origLen + 8) >>> 6) * 64 + 56 + 8;
  const msg = new Uint8Array(newLen);
  msg.set(data);
  msg[origLen] = 0x80;

  // Append original length in bits as 64-bit little-endian
  const bitLen = origLen * 8;
  const view = new DataView(msg.buffer);
  view.setUint32(newLen - 8, bitLen >>> 0, true);
  view.setUint32(newLen - 4, Math.floor(bitLen / 0x100000000), true);

  // Initialize hash values
  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;

  // Process each 64-byte chunk
  for (let i = 0; i < newLen; i += 64) {
    const M = new Uint32Array(16);
    for (let j = 0; j < 16; j++) {
      M[j] = view.getUint32(i + j * 4, true);
    }

    let A = a0, B = b0, C = c0, D = d0;

    for (let j = 0; j < 64; j++) {
      let F: number, g: number;
      if (j < 16) {
        F = (B & C) | (~B & D);
        g = j;
      } else if (j < 32) {
        F = (D & B) | (~D & C);
        g = (5 * j + 1) % 16;
      } else if (j < 48) {
        F = B ^ C ^ D;
        g = (3 * j + 5) % 16;
      } else {
        F = C ^ (B | ~D);
        g = (7 * j) % 16;
      }
      F = (F + A + K[j] + M[g]) >>> 0;
      A = D;
      D = C;
      C = B;
      B = (B + leftRotate(F, S[j])) >>> 0;
    }

    a0 = (a0 + A) >>> 0;
    b0 = (b0 + B) >>> 0;
    c0 = (c0 + C) >>> 0;
    d0 = (d0 + D) >>> 0;
  }

  // Produce the final hash value (little-endian)
  const result = new Uint8Array(16);
  const resultView = new DataView(result.buffer);
  resultView.setUint32(0, a0, true);
  resultView.setUint32(4, b0, true);
  resultView.setUint32(8, c0, true);
  resultView.setUint32(12, d0, true);

  return result;
}

/**
 * EVP_BytesToKey - OpenSSL 密钥派生函数
 * CryptoJS 使用此算法从密码和 salt 派生 key 和 IV
 */
function evpBytesToKey(
  password: Uint8Array,
  salt: Uint8Array,
  keyLen: number,
  ivLen: number
): { key: Uint8Array; iv: Uint8Array } {
  const totalLen = keyLen + ivLen;
  const result = new Uint8Array(totalLen);
  let resultOffset = 0;
  let prevHash = new Uint8Array(0);

  while (resultOffset < totalLen) {
    // Concatenate: prevHash + password + salt
    const data = new Uint8Array(prevHash.length + password.length + salt.length);
    data.set(prevHash, 0);
    data.set(password, prevHash.length);
    data.set(salt, prevHash.length + password.length);

    // Hash with MD5
    prevHash = md5(data);

    // Copy to result
    const copyLen = Math.min(prevHash.length, totalLen - resultOffset);
    result.set(prevHash.slice(0, copyLen), resultOffset);
    resultOffset += copyLen;
  }

  return {
    key: result.slice(0, keyLen),
    iv: result.slice(keyLen, keyLen + ivLen),
  };
}

/**
 * Base64 解码
 */
function base64Decode(str: string): Uint8Array {
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * 移除 PKCS7 填充
 */
function removePkcs7Padding(data: Uint8Array): Uint8Array {
  if (data.length === 0) return data;
  const padLen = data[data.length - 1];
  if (padLen > 16 || padLen === 0) return data;
  // Verify padding
  for (let i = data.length - padLen; i < data.length; i++) {
    if (data[i] !== padLen) return data;
  }
  return data.slice(0, data.length - padLen);
}

/**
 * AES-CBC 解密（兼容 CryptoJS/OpenSSL 格式）
 *
 * CryptoJS 加密格式：
 * - Base64 编码
 * - 解码后：'Salted__' (8 bytes) + salt (8 bytes) + ciphertext
 *
 * @param encryptedBase64 Base64 编码的加密数据
 * @param password 密码字符串
 * @returns 解密后的明文字符串
 */
export async function aesDecrypt(
  encryptedBase64: string,
  password: string
): Promise<string> {
  try {
    // 1. Base64 decode
    const encrypted = base64Decode(encryptedBase64);

    // 2. Check for 'Salted__' prefix
    const salted = new TextDecoder().decode(encrypted.slice(0, 8));
    if (salted !== 'Salted__') {
      throw new Error('Invalid encrypted data format: missing Salted__ prefix');
    }

    // 3. Extract salt and ciphertext
    const salt = encrypted.slice(8, 16);
    const ciphertext = encrypted.slice(16);

    // 4. Derive key and IV using EVP_BytesToKey
    const passwordBytes = new TextEncoder().encode(password);
    const { key, iv } = evpBytesToKey(passwordBytes, salt, 32, 16); // AES-256-CBC

    // 5. Import key for Web Crypto API
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      key,
      { name: 'AES-CBC' },
      false,
      ['decrypt']
    );

    // 6. Decrypt
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      cryptoKey,
      ciphertext
    );

    // 7. Remove PKCS7 padding and convert to string
    const decryptedBytes = removePkcs7Padding(new Uint8Array(decrypted));
    return new TextDecoder().decode(decryptedBytes);
  } catch (error) {
    console.error('[CryptoUtils] AES decrypt failed:', error);
    throw new Error('Decryption failed');
  }
}

/**
 * 检查字符串是否是加密格式（CryptoJS/OpenSSL 格式）
 * 加密格式：Base64 编码，解码后以 'Salted__' 开头
 *
 * @param str 待检查的字符串
 * @returns 是否是加密格式
 */
export function isEncryptedPeer(str: string): boolean {
  try {
    const decoded = atob(str);
    return decoded.startsWith('Salted__');
  } catch {
    return false;
  }
}

/**
 * 解密 URL 中的 peer 参数
 * 使用公网 IP 作为密钥（防止用户直接复制链接给他人使用）
 *
 * @param encryptedPeer URL 中的加密 peer 值
 * @returns 解密后的真实 peer ID
 */
export async function decryptPeer(encryptedPeer: string): Promise<string> {
  if(encryptedPeer.length < 15){
    return encryptedPeer
  }

  const publicIP = await getPublicIP();
  return aesDecrypt(encryptedPeer, publicIP);
}
